import { useMemo } from 'react'
import css from './views.module.css'
import { Card, Disclosure, Empty, MicroLabel, Notice, QueryError, ViewHeader } from '../ui'
import { useComponents } from '../data/queries'
import {
  freshnessOf,
  groupComponents,
  type Component,
  type Fact,
  type Interaction,
} from '../data/map/components'
import { componentGroupLine, componentLine } from '../data/map/summaries'
import { parseMarkdown } from '../lib/markdown'
import { Blocks } from './Markdown'

/**
 * Every component of the fleet, and what it touches.
 *
 * The question this page exists to answer is "can this thing email a
 * customer", asked by someone who has just taken the fleet over and is not
 * going to read Python to find out. So Reads, Writes and Sends are three lists
 * and never one, and an empty list says what its emptiness means: "changes
 * nothing outside itself" is an answer, and a blank space is not.
 *
 * Nothing on this page was worked out here. `public.fleet_components` is
 * published from `98h-agents` by a timer on the box, from a descriptor beside
 * each component's code that CI checks against the code. The view renders it
 * and states how old it is -- a directory that has quietly stopped being
 * republished looks exactly like a fleet that has stopped changing, and the
 * second is a much nicer thing to believe.
 *
 * No kind, heading or order is written here. They come out of the rows, the
 * same way the department list does, so a kind nobody has thought of yet gets
 * its own heading in the right place with no code change.
 */
export function Directory() {
  const components = useComponents()
  const rows = useMemo(() => components.data ?? [], [components.data])
  const groups = useMemo(() => groupComponents(rows), [rows])
  const freshness = useMemo(() => freshnessOf(rows), [rows])

  if (components.error) return <QueryError error={components.error} />
  if (!components.data) {
    return <Card><div style={{ padding: 22, color: 'var(--ink-3)' }}>Reading…</div></Card>
  }

  return (
    <>
      <ViewHeader title="Directory" aside={freshness.sentence} />

      {groups.length === 0 ? (
        <Card>
          <Empty glyph="[-_-]">
            Nothing published. Either the inventory publisher has not run yet, or it has stopped —
            an empty directory is the one thing it refuses to write, so a blank page here means the
            table was never filled rather than that the fleet is empty.
          </Empty>
        </Card>
      ) : (
        groups.map((group) => (
          <section key={group.kind} className={css.dirGroup}>
            {/* Headings open, entries closed: the page reads as a list of what
                each component is and what it touches, and opens to the rest. */}
            <Disclosure flush title={group.label} summary={componentGroupLine(group)} defaultOpen>
              {/* Deliberately not the Card primitive: at phone width this
                  dissolves so each component becomes its own card (rule 5). */}
              <div className={css.dirList}>
                {group.components.map((component) => (
                  <ComponentView key={component.slug} component={component} />
                ))}
              </div>
            </Disclosure>
          </section>
        ))
      )}

      <Notice>
        Published from <span className="mono">98-Ventures/98h-agents</span> — one descriptor beside
        each component's code, checked against that code by CI. Adding a component is a pull
        request there, not a button here. Secrets are listed by name and origin only; no value is
        published and none is read by this page.
      </Notice>
    </>
  )
}

function ComponentView({ component }: { component: Component }) {
  // Agent prose, rendered to text nodes. There is no dangerouslySetInnerHTML
  // anywhere in this app and this is not where one starts.
  const description = useMemo(
    () => (component.description ? parseMarkdown(component.description).blocks : null),
    [component.description],
  )

  return (
    <article className={css.dirItem}>
      {/* Closed, an entry is its name, its one-line summary and what it
          touches as counts -- enough to answer "can this email a customer"
          by whether it says "sends nothing". Open, the lists name each one. */}
      <Disclosure
        flush
        title={
          <span className={css.dirHead}>
            <span className={css.dirName}>{component.name}</span>
            <span className={`${css.dirSlug} mono`}>{component.slug}</span>
          </span>
        }
        summary={
          <>
            <span className={css.dirMeta}>
              {/* A status is a word, never a badge and never a count. 'paused' and
                  'retired' are muted states here, not alarms (product rule 1). */}
              <span className={component.status === 'live' ? css.dirStatus : css.dirStatusQuiet}>
                {component.status}
              </span>
              {component.maintainer ? <span>maintained by {component.maintainer}</span> : null}
              {component.model ? <span className="mono">{component.model}</span> : null}
              <span>{componentLine(component)}</span>
            </span>
            <span className={css.dirSummary}>{component.summary}</span>
          </>
        }
      >
        {description ? (
          <div className={css.dirProse}>
            <Blocks blocks={description} />
          </div>
        ) : null}

        <p className={css.dirRuns}>{component.recordsRunsNote}</p>

        <div className={css.dirTouches}>
          {component.touches.map((list) => (
            <section key={list.key} className={css.dirTouch}>
              <MicroLabel>{list.label}</MicroLabel>
              {list.entries.length === 0 ? (
                <p className={css.dirNone}>{list.empty}</p>
              ) : (
                list.entries.map((entry, index) => <InteractionView key={index} entry={entry} />)
              )}
            </section>
          ))}
        </div>

        <div className={css.dirBlocks}>
          <section className={css.dirBlock}>
            <MicroLabel>How it runs</MicroLabel>
            {component.invocation.type ? (
              <div className={`${css.dirEntryHead} mono`}>{component.invocation.type}</div>
            ) : null}
            <Facts facts={component.invocation.facts} />
            {component.invocation.credentials ? (
              <>
                <div className={css.dirSubLabel}>Credentials</div>
                <Facts facts={component.invocation.credentials} />
              </>
            ) : (
              <p className={css.dirNone}>No credentials are injected when it runs.</p>
            )}
          </section>

          <section className={css.dirBlock}>
            <MicroLabel>Secrets</MicroLabel>
            {component.secrets.length === 0 ? (
              <p className={css.dirNone}>Holds no secrets.</p>
            ) : (
              <ul className={css.dirSecrets}>
                {component.secrets.map((secret, index) => (
                  <li key={index}>
                    <span className="mono">{secret.name}</span>
                    {secret.from ? <span className={css.dirFrom}> from {secret.from}</span> : null}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {component.extra.length > 0 ? (
            <section className={css.dirBlock}>
              {/* The open column. Whatever a descriptor grows next shows up here
                  labelled by its own key rather than being dropped. */}
              <MicroLabel>Also recorded</MicroLabel>
              <Facts facts={component.extra} />
            </section>
          ) : null}
        </div>

        <div className={css.dirLinks}>
          {component.docUrl ? (
            <a href={component.docUrl} target="_blank" rel="noreferrer">Documentation →</a>
          ) : null}
          {component.repo ? (
            <span className={`${css.dirPath} mono`}>
              {component.repo}{component.path ? ` · ${component.path}` : ''}
            </span>
          ) : null}
        </div>
      </Disclosure>
    </article>
  )
}

function InteractionView({ entry }: { entry: Interaction }) {
  return (
    <div className={css.dirEntry}>
      <div className={css.dirEntryHead}>
        {entry.system ? <span className="mono">{entry.system}</span> : null}
        {entry.target ? <span className={css.dirTarget}>{entry.target}</span> : null}
      </div>
      {entry.tables.length > 0 ? (
        <div className={`${css.dirTables} mono`}>{entry.tables.join(', ')}</div>
      ) : null}
      {entry.what ? <p className={css.dirWhat}>{entry.what}</p> : null}
      {entry.actorLabel ? (
        // Who performs it is a different fact, not a shade of the same one:
        // the Equip planner reads, and its documented monthly procedure has a
        // person running an update.
        <div className={entry.byHuman ? css.dirActorHuman : css.dirActor}>{entry.actorLabel}</div>
      ) : null}
      <Facts facts={entry.facts} />
    </div>
  )
}

function Facts({ facts }: { facts: Fact[] }) {
  if (facts.length === 0) return null
  return (
    <dl className={css.dirFacts}>
      {facts.map((fact, index) => (
        <div key={index} className={css.dirFact}>
          <dt className={css.dirFactLabel}>{fact.label}</dt>
          <dd className={css.dirFactValue}>{fact.value}</dd>
        </div>
      ))}
    </dl>
  )
}
